import React, { useState, useEffect } from 'react';

const ChessBoard = ({ gameState, isMyTurn, onMove, currentUserId }) => {
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  
  const board = gameState?.board || [];
  const winner = gameState?.winner;
  const isDraw = gameState?.isDraw;
  const isGameOver = winner || isDraw;
  const isCheck = gameState?.isCheck;
  
  // Determine player color (white = uppercase, black = lowercase)
  const isWhite = gameState?.player1_id === currentUserId;
  
  const pieceSymbols = {
    'K': '♔', 'Q': '♕', 'R': '♖', 'B': '♗', 'N': '♘', 'P': '♙',
    'k': '♚', 'q': '♛', 'r': '♜', 'b': '♝', 'n': '♞', 'p': '♟'
  };
  
  useEffect(() => {
    if (selectedPiece) {
      const moves = calculateValidMoves(selectedPiece.row, selectedPiece.col);
      setValidMoves(moves);
    } else {
      setValidMoves([]);
    }
  }, [selectedPiece, board]);
  
  const isMyPiece = (piece) => {
    if (!piece) return false;
    if (isWhite) return piece === piece.toUpperCase();
    return piece === piece.toLowerCase();
  };
  
  const calculateValidMoves = (row, col) => {
    const piece = board[row][col];
    if (!piece || !isMyPiece(piece)) return [];
    
    const moves = [];
    const pieceType = piece.toLowerCase();
    const direction = isWhite ? -1 : 1; // White moves up (negative), black moves down (positive)
    
    switch (pieceType) {
      case 'p': // Pawn
        // Forward move
        const forwardRow = row + direction;
        if (forwardRow >= 0 && forwardRow < 8 && !board[forwardRow][col]) {
          moves.push({ row: forwardRow, col });
          
          // Double move from starting position
          const startRow = isWhite ? 6 : 1;
          const doubleRow = row + direction * 2;
          if (row === startRow && !board[doubleRow][col]) {
            moves.push({ row: doubleRow, col });
          }
        }
        
        // Diagonal captures
        for (const dc of [-1, 1]) {
          const newCol = col + dc;
          if (newCol >= 0 && newCol < 8 && forwardRow >= 0 && forwardRow < 8) {
            const target = board[forwardRow][newCol];
            if (target && !isMyPiece(target)) {
              moves.push({ row: forwardRow, col: newCol });
            }
          }
        }
        break;
        
      case 'n': // Knight
        const knightMoves = [
          [-2, -1], [-2, 1], [-1, -2], [-1, 2],
          [1, -2], [1, 2], [2, -1], [2, 1]
        ];
        for (const [dr, dc] of knightMoves) {
          const newRow = row + dr;
          const newCol = col + dc;
          if (newRow >= 0 && newRow < 8 && newCol >= 0 && newCol < 8) {
            const target = board[newRow][newCol];
            if (!target || !isMyPiece(target)) {
              moves.push({ row: newRow, col: newCol });
            }
          }
        }
        break;
        
      case 'b': // Bishop
        addSlidingMoves(row, col, [[-1, -1], [-1, 1], [1, -1], [1, 1]], moves);
        break;
        
      case 'r': // Rook
        addSlidingMoves(row, col, [[-1, 0], [1, 0], [0, -1], [0, 1]], moves);
        break;
        
      case 'q': // Queen
        addSlidingMoves(row, col, [
          [-1, -1], [-1, 0], [-1, 1],
          [0, -1], [0, 1],
          [1, -1], [1, 0], [1, 1]
        ], moves);
        break;
        
      case 'k': // King
        const kingMoves = [
          [-1, -1], [-1, 0], [-1, 1],
          [0, -1], [0, 1],
          [1, -1], [1, 0], [1, 1]
        ];
        for (const [dr, dc] of kingMoves) {
          const newRow = row + dr;
          const newCol = col + dc;
          if (newRow >= 0 && newRow < 8 && newCol >= 0 && newCol < 8) {
            const target = board[newRow][newCol];
            if (!target || !isMyPiece(target)) {
              moves.push({ row: newRow, col: newCol });
            }
          }
        }
        break;
    }
    
    return moves;
  };
  
  const addSlidingMoves = (row, col, directions, moves) => {
    for (const [dr, dc] of directions) {
      let newRow = row + dr;
      let newCol = col + dc;
      
      while (newRow >= 0 && newRow < 8 && newCol >= 0 && newCol < 8) {
        const target = board[newRow][newCol];
        
        if (!target) {
          moves.push({ row: newRow, col: newCol });
        } else {
          if (!isMyPiece(target)) {
            moves.push({ row: newRow, col: newCol });
          }
          break;
        }
        
        newRow += dr;
        newCol += dc;
      }
    }
  };
  
  const handleSquareClick = (row, col) => {
    if (isGameOver || !isMyTurn) return;
    
    const piece = board[row][col];
    
    // If clicking on own piece, select it
    if (piece && isMyPiece(piece)) {
      setSelectedPiece({ row, col });
      return;
    }
    
    // If clicking on valid move destination
    if (selectedPiece) {
      const move = validMoves.find(m => m.row === row && m.col === col);
      if (move) {
        // Check for pawn promotion
        const movingPiece = board[selectedPiece.row][selectedPiece.col];
        const isPawn = movingPiece.toLowerCase() === 'p';
        const isPromotionRow = (isWhite && row === 0) || (!isWhite && row === 7);
        
        onMove({
          fromRow: selectedPiece.row,
          fromCol: selectedPiece.col,
          toRow: row,
          toCol: col,
          promotion: (isPawn && isPromotionRow) ? 'q' : undefined
        });
        setSelectedPiece(null);
      }
    }
  };
  
  const isValidMoveSquare = (row, col) => {
    return validMoves.some(m => m.row === row && m.col === col);
  };
  
  const isSelected = (row, col) => {
    return selectedPiece?.row === row && selectedPiece?.col === col;
  };
  
  return (
    <div className="p-3 sm:p-4 md:p-6 flex flex-col items-center gap-3 sm:gap-4 md:gap-6">
      {/* Status */}
      <div className="text-center">
        {isGameOver ? (
          <div className="space-y-2">
            {winner ? (
              <>
                <p className="text-2xl font-bold text-emerald-400">
                  {winner === currentUserId ? ' Checkmate! You Won!' : ' Checkmate! You Lost'}
                </p>
                <p className="text-sm text-white/50">
                  {winner === currentUserId ? 'The king has fallen!' : 'Your king was captured'}
                </p>
              </>
            ) : isDraw ? (
              <>
                <p className="text-xl text-yellow-400">It's a Draw!</p>
                <p className="text-sm text-white/50">Stalemate</p>
              </>
            ) : null}
          </div>
        ) : (
          <div className="space-y-1">
            <div className={`text-lg font-semibold ${isMyTurn ? 'text-emerald-400' : 'text-white/50'}`}>
              {isMyTurn ? 'Your Turn' : 'Opponent\'s Turn'}
            </div>
            {isCheck && isMyTurn && (
              <p className="text-sm text-red-400 animate-pulse font-semibold"> Check! Protect your king!</p>
            )}
            {selectedPiece && (
              <p className="text-xs text-white/40">Click a highlighted square to move</p>
            )}
          </div>
        )}
      </div>
      
      {/* Board */}
      <div className="relative">
        {/* 8x8 Grid */}
        <div className="grid grid-cols-8 gap-0 border-4 border-white/20 rounded-lg overflow-hidden shadow-2xl">
          {board.map((row, rowIndex) => (
            row.map((piece, colIndex) => {
              const isLight = (rowIndex + colIndex) % 2 === 0;
              const isValidMove = isValidMoveSquare(rowIndex, colIndex);
              const selected = isSelected(rowIndex, colIndex);
              const canSelect = piece && isMyPiece(piece) && isMyTurn && !isGameOver;
              
              return (
                <button
                  key={`${rowIndex}-${colIndex}`}
                  onClick={() => handleSquareClick(rowIndex, colIndex)}
                  disabled={isGameOver || !isMyTurn}
                  className={`
                    w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 flex items-center justify-center relative
                    transition-all duration-200
                    ${isLight ? 'bg-amber-100' : 'bg-emerald-900'}
                    ${selected ? 'ring-4 ring-blue-400 ring-inset' : ''}
                    ${isValidMove ? 'ring-4 ring-yellow-400 ring-inset' : ''}
                    ${canSelect ? 'hover:brightness-110 cursor-pointer' : ''}
                  `}
                >
                  {piece && (
                    <span className={`
                      text-5xl leading-none select-none
                      ${piece === piece.toUpperCase() ? 'text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]' : 'text-slate-900'}
                      transition-transform hover:scale-110
                    `}>
                      {pieceSymbols[piece] || piece}
                    </span>
                  )}
                  
                  {/* Valid move indicator */}
                  {isValidMove && (
                    <div className="absolute inset-0 bg-yellow-400/20 flex items-center justify-center pointer-events-none">
                      <div className={`
                        rounded-full bg-yellow-400/60
                        ${piece ? 'w-12 h-12 border-4 border-yellow-400' : 'w-4 h-4'}
                      `} />
                    </div>
                  )}
                </button>
              );
            })
          ))}
        </div>
        
        {/* Row/Column Labels */}
        <div className="absolute -left-6 top-0 h-full flex flex-col justify-around text-white/30 text-xs font-mono">
          {[8, 7, 6, 5, 4, 3, 2, 1].map(num => (
            <div key={num}>{num}</div>
          ))}
        </div>
        <div className="absolute -bottom-6 left-0 w-full flex justify-around text-white/30 text-xs font-mono">
          {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map(letter => (
            <div key={letter}>{letter}</div>
          ))}
        </div>
      </div>
      
      {/* Legend */}
      <div className="flex gap-6 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-2xl text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">♔</span>
          <span className="text-white/70">White</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl text-slate-900">♚</span>
          <span className="text-white/70">Black</span>
        </div>
      </div>
    </div>
  );
};

export default ChessBoard;
